import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { Message, SelectItem } from 'primeng/api';
import { MessageService } from '@shared/services/message-service';
import { ProductService } from '../../../../services/product.service';
import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { tap, filter } from 'rxjs/operators';
import { InvCountProductCount } from '../../../../models/inv-count-product-count';
import { BayType } from '@shared/models/bay-type';
import { InventoryService } from '../../../../services/inventory.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { SubinventoryType } from '@shared/models/subinventory';
import { Product } from '@shared/models/product';
import { ShiftStatus } from '@shared/models/worked-shift';
import { ShiftContext } from '../../../../models/shift-context';
import { saveDebounce } from '@shared/helpers/save-debounce';

type SupplyItemForm = FormGroup<{
    id: FormControl<number>;
    bayType: FormControl<BayType>;
    productId: FormControl<number>;
    pieces1: FormControl<number>;
    allocatedFromPlus: FormControl<number>;
}>;

@Component({
    selector: 'app-supplies-inventory',
    templateUrl: './supplies-inventory.component.html',
    styleUrls: ['./supplies-inventory.component.scss']
})
export class SuppliesInventoryComponent implements OnInit, OnDestroy {

    @Input() invCountId: number;
    formBuilder: FormBuilder = new FormBuilder();
    @Input() set subinventoryId(subinventoryId: number) {
        this.siteSubinventoryId.next(subinventoryId);
    }

    subInvType: SubinventoryType = SubinventoryType.ChepComponents;
    isChepComponents: boolean = false;
    addForm = new FormGroup( {
        bayType: new FormControl<BayType>(null, Validators.required),
        productId: new FormControl<number>(null, Validators.required),
        pieces1: new FormControl<number>(0, Validators.required),
        allocatedFromPlus: new FormControl<number>(0, Validators.required)
    });

    bayTypes = BayType;
    bayTypesSelect: SelectItem[] = [];
    productSelect: SelectItem[] = [];
    products: Product[];
    productUnits: Map<number, string> = new Map<number, string>();
    subscriptions: Subscription[];
    shutdown: Subject<void> = new Subject<void>();
    siteSubinventoryId = new BehaviorSubject<number>(null);
    volumeUOM: string = '';

    supplyInventoryItems: SupplyItemForm[];
    updateSubscriptions: Subscription[] = [];

    context: ShiftContext;

    readonly _supplyInventoryItemNotCreatedMsg: Message = {
        severity: 'warn',
        summary: 'Could not Create Supply Inventory Item',
        detail: 'Fields are required.'
    };

    readonly _supplyInventoryItemCreatedMsg: Message = {
        severity: 'success',
        summary: 'Supply Inventory Item Created',
        detail: ''
    };

    readonly _supplyInventoryItemDeletedMsg: Message = {
        severity: 'warn',
        summary: 'Supply Inventory Item Deleted',
        detail: ''
    };

    constructor(
        private inventoryService: InventoryService,
        private productService: ProductService,
        private messageService: MessageService,
        private shiftContextService: ShiftContextService,
        private activityService: ActivityBlockingService) {
    }

    ngOnInit(): void {
        this.supplyInventoryItems = [];

        const validBayTypes = [
            { value: BayType.PlantSupplyPlusLine, label: 'Plus' },
            { value: BayType.PlantSupplyPremLine, label: 'Premium' },
            { value: BayType.PlantSupplyCustLine, label: 'Custom' },
            { value: BayType.PlantSupplyMultiLine, label: 'Multicolor' },
            { value: BayType.WhiteWoodSupplyLine, label: 'White Wood' },
            { value: BayType.PecoSupplyLine, label: 'PECO' }
        ];

        const bayTypesObservable = this.productService.ComponentBayTypesForSubinventory(this.siteSubinventoryId)
            .pipe(tap(bayTypes => {
                this.bayTypesSelect = validBayTypes.filter(bt => bayTypes.some(t => t === bt.value));
            }));

        const siteObservable = this.shiftContextService.CurrentContext.pipe(tap(context => {
            this.context = context;

            this.subInvType = context.site.subinventories
                .find(s => s.id === this.siteSubinventoryId.value)
                ?.subinventoryType;
            this.isChepComponents = this.subInvType === SubinventoryType.ChepComponents;
            this.onRepairTypeChange();

            if (context.status !== ShiftStatus.Open) {
                this.addForm.disable({ emitEvent: false });
                this.supplyInventoryItems.forEach(form => form.disable({ emitEvent: false }));
            }
            else {
                this.addForm.enable({ emitEvent: false });
                this.supplyInventoryItems.forEach(form => form.enable({ emitEvent: false }));
            }
        }));

        const productsObservable = this.productService.ProductsForSubinventory(this.siteSubinventoryId)
            .pipe(tap(products => {
                this.products = products;
                this.productSelect = products
                    .filter(p => p.productType === 'Component - Supply')
                    .map(p => ({ value: p.id, label: p.label }));
            }));

        const suppliesInventoryObservable = this.inventoryService.CurrentInvProductCount.pipe(
            filter(counts => !!counts),
            tap(counts => {
                this.supplyInventoryItems = counts
                    .filter(x => validBayTypes.some(bt => bt.value === x.bayType))
                    .map(i => this.addFormGroup(i));

                if (this.context?.status !== ShiftStatus.Open)
                    this.supplyInventoryItems.forEach(form => form.disable({ emitEvent: false }));
                else
                    this.supplyInventoryItems.forEach(form => form.enable({ emitEvent: false }));

                this.updateSubscriptions.forEach(s => s.unsubscribe());
                this.updateSubscriptions = [
                    ...this.supplyInventoryItems.map(form => form.valueChanges
                        .pipe(saveDebounce(this.shutdown))
                        .subscribe(val => this.updateSupplyCount({
                            id: val.id,
                            invCountId: this.invCountId,
                            productId: val.productId,
                            bayType: val.bayType,
                            pieces1: val.pieces1,
                            allocatedFromPlus: val.allocatedFromPlus
                        }))),
                    ...this.supplyInventoryItems
                        .map(form => form.controls.bayType.valueChanges
                            .subscribe(bayType => {
                                if (bayType === BayType.PlantSupplyPlusLine || bayType === BayType.WhiteWoodSupplyLine)
                                    form.controls.allocatedFromPlus.disable();
                                else
                                    form.controls.allocatedFromPlus.enable();
                            }))
                ];
            }));

        this.subscriptions = [
            combineLatest([siteObservable, productsObservable, bayTypesObservable, suppliesInventoryObservable])
                .pipe(this.activityService.Wait('Supplies Inventory'))
                .subscribe(([so, po, bto, cio]) => {
                    if (!this.isChepComponents) {
                        this.addForm.controls.bayType.setValue(this.bayTypesSelect[0].value);
                        this.addForm.controls.bayType.clearValidators();
                        this.addForm.controls.bayType.updateValueAndValidity();
                        this.addForm.controls.bayType.disable();
                    }
                })
        ];
    }

    ngOnDestroy() {
        this.shutdown.next();
        this.subscriptions.forEach(s => s.unsubscribe());
        this.updateSubscriptions.forEach(s => s.unsubscribe());
        this.updateSubscriptions = [];
    }

    updateSupplyCount(supplyInventoryItem: InvCountProductCount) {
        this.inventoryService.updateInvCountProductCount(supplyInventoryItem).subscribe(_updated => {
            this.messageService.add({
                severity: 'success',
                summary: 'Supply Inventory Updated',
                detail: ''
            });
        });
    }

    saveSI(): void {
        if (!this.addForm.valid) {
            this.addForm.markAllAsTouched();
            this.messageService.add(this._supplyInventoryItemNotCreatedMsg);
        } else {
            const entry: InvCountProductCount = {
                id: 0,
                invCountId: this.invCountId,
                productId: this.addForm.controls.productId.value,
                bayType: this.addForm.controls.bayType.value,
                pieces1: this.addForm.controls.pieces1.value,
                allocatedFromPlus: this.addForm.controls.allocatedFromPlus.value
            };

            this.inventoryService.createInvCountProductCount(entry).subscribe(_created => {
                const form = this.addFormGroup(_created);
                this.supplyInventoryItems = [ ...this.supplyInventoryItems, form ];

                this.updateSubscriptions.push(form.valueChanges
                    .pipe(saveDebounce(this.shutdown))
                    .subscribe(val => this.updateSupplyCount({
                        id: val.id,
                        invCountId: this.invCountId,
                        productId: val.productId,
                        bayType: val.bayType,
                        pieces1: val.pieces1,
                        allocatedFromPlus: val.allocatedFromPlus
                    })),
                    form.controls.bayType.valueChanges
                        .subscribe(bayType => {
                            if (bayType === BayType.PlantSupplyPlusLine || bayType === BayType.WhiteWoodSupplyLine)
                                form.controls.allocatedFromPlus.disable();
                            else
                                form.controls.allocatedFromPlus.enable();
                        }));

                this.addForm.reset({
                    bayType: this.isChepComponents ? null : this.bayTypesSelect[0].value,
                    productId: null,
                    pieces1: 0,
                    allocatedFromPlus: 0});
                this.messageService.add(this._supplyInventoryItemCreatedMsg);
            });
        }
    }

    addFormGroup(item: InvCountProductCount) {
       return new FormGroup({
            id: new FormControl<number>(item.id),
            bayType: new FormControl<BayType>(item.bayType, Validators.required),
            productId: new FormControl<number>(item.productId, Validators.required),
            pieces1: new FormControl<number>(item.pieces1, Validators.required),
            allocatedFromPlus: new FormControl<number>(item.allocatedFromPlus, Validators.required)
        });
    }

    deleteRow(supplyInventoryItem: SupplyItemForm): void {
        this.inventoryService.deleteInvCountProductCount(supplyInventoryItem.controls.id.value).subscribe(() => {
            this.supplyInventoryItems.splice(this.supplyInventoryItems.indexOf(supplyInventoryItem), 1);
            this.messageService.add(this._supplyInventoryItemDeletedMsg);
        });
    }

    rowTrackBy(_i: number, item: SupplyItemForm): number {
        return item.controls.id.value;
    }

    onRepairTypeChange(): void {
        const allocField = this.addForm.controls.allocatedFromPlus;

        if (!this.isChepComponents || !this.isAllocRequiredBayType()) {
            if (+allocField.value !== 0)
                allocField.setValue(0);

            allocField.clearValidators();
            allocField.updateValueAndValidity({ emitEvent: false });
            allocField.disable({ emitEvent: false });
        }
        else {
            allocField.enable({ emitEvent: false });
            allocField.setValidators(Validators.required);
            allocField.updateValueAndValidity({ emitEvent: false });
        }
    }

    onProductChange(): void {
        this.volumeUOM = this.getVolumeUOM(this.addForm.controls.productId.value);
    }

    isAllocRequiredBayType(): boolean {
        const value = this.addForm.controls.bayType.value;
        return this.isChepComponents && !(value === BayType.PlantSupplyPlusLine || value === BayType.WhiteWoodSupplyLine);
    }

    getVolumeUOM(productId: number): string {
        if (this.products)
            return this.products.find(p => p.id === productId)?.volumeUnits ?? '';
        else
            return '';
    }

    protected readonly SubinventoryType = SubinventoryType;
}
